// B-162 — "Cierre de conciliación", in Reportes: the client's close over a range of days, the one the
// operation reports daily (Nubia to Willinton) and today types into a large workbook by hand.
//
// For a range — "Ayer" by default, the day the close is reported — it says how many closings
// reconciled, how many alerts stand open or were attended and what the differences are worth, and,
// day by day, the offices' total against the correspondent account. Under it, the internal view
// Mariana offered for operations: the offices with most alerts and the alerts by type. A day opens
// that day in the Panel. "Exportar Excel" writes the three tables.
//
// Drawn only for a client with a day reconciliation (Banco Agrario): any other is answered 404 and
// nothing is shown; a person who does not see the whole client is answered 403, said in one line. The
// pure half is `utils/cierreReporteConciliacion.js`.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { CalendarCheck2, CheckCircle2, Download, TriangleAlert, Wallet } from "lucide-react";
import { Button, Spinner, cn } from "@mantis-sas/ui";

import { getCierreReconciliationReport } from "../../../ApiCalls/mantisBusiness";
import { correctionError } from "../../../utils/cierreCorrections";
import { fechaDMY, montoLegible } from "../../../utils/cierrePanel";
import { formatMoney } from "../../../utils/formatMoney";
import { descargarTablas } from "../../../utils/excelTabla";
import { nombreDeAlerta } from "../../../utils/cierreNombres";
import { useNombresCierre } from "../../../hooks/useNombresCierre";
import {
  RANGOS, deTotal, hojasDeReporte, problemaDeRango, puntosDeAlertas, rangoDeReporte,
} from "../../../utils/cierreReporteConciliacion";
import {
  BarraDeFiltros, Cifra, Filtro, LugarDeGrafica, SELECT_DE_FILTRO, Segmentado, Tabla, Tarjeta, celda, enPalabras, fila,
  leyendaLegible,
} from "./CierresVisual";

const EJE = {
  tickLine: false, axisLine: false, fontSize: 11, tick: { fill: "var(--brinks-muted-foreground)" },
};
const monto = (v) => (v === null || v === undefined ? "—" : montoLegible(v));

export function CierreConciliacion({ token, mantisBusinessId, clientCode, puedeExportar }) {
  const navigate = useNavigate();
  const nombres = useNombresCierre(token, mantisBusinessId, clientCode);
  const [rango, setRango] = useState("ayer");
  const [propio, setPropio] = useState({ from: "", to: "" });
  const [pedido, setPedido] = useState(() => rangoDeReporte("ayer"));
  const [datos, setDatos] = useState(null);
  const [estado, setEstado] = useState("cargando");
  const [error, setError] = useState(null);
  const [exportando, setExportando] = useState(false);

  useEffect(() => {
    if (!clientCode) return undefined;
    let activo = true;
    setEstado("cargando");
    setError(null);
    getCierreReconciliationReport(token, mantisBusinessId, clientCode, pedido)
      .then((res) => {
        if (!activo) return;
        if (res?.success) {
          setDatos(res.data);
          setEstado("listo");
        } else if (res?.status === 404 || res?.errorCode === "NOT_FOUND") {
          setEstado("sin");
        } else if (res?.status === 403 || res?.errorCode === "ACTION_FORBIDDEN") {
          setEstado("sinPermiso");
        } else {
          setError(correctionError(res));
          setEstado("error");
        }
      })
      .catch(() => {
        if (!activo) return;
        setError("No se pudo cargar el cierre de conciliación.");
        setEstado("error");
      });
    return () => { activo = false; };
  }, [token, mantisBusinessId, clientCode, pedido]);

  if (!clientCode || estado === "sin") return null;
  if (estado === "sinPermiso") {
    return (
      <p className="rounded-[12px] border bg-card px-4 py-3 text-[12.5px] text-brinks-muted-foreground">
        El cierre de conciliación suma todas las oficinas del cliente; tu acceso no cubre el cliente completo.
      </p>
    );
  }

  const elegir = (clave) => {
    setRango(clave);
    setPedido(rangoDeReporte(clave));
  };
  const problema = problemaDeRango(propio);
  const exportar = async () => {
    setExportando(true);
    try {
      await descargarTablas(
        `Cierre de conciliación ${clientCode} ${datos.period.from} a ${datos.period.to}.xlsx`,
        hojasDeReporte(datos, (clave) => nombreDeAlerta(nombres, clave)),
      );
    } finally {
      setExportando(false);
    }
  };
  const t = datos?.totals;

  return (
    <section className="min-w-0 space-y-3 rounded-[16px] border bg-brinks-surface p-3" aria-label="Cierre de conciliación">
      <header className="space-y-3 px-1">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <h2 className="text-[16px] font-semibold text-brinks-foreground">Cierre de conciliación</h2>
            <p className="mt-0.5 text-[12.5px] text-brinks-muted-foreground">
              Cómo cerró cada día: cierres cuadrados, alertas y la cuenta del corresponsal
              {datos ? ` · del ${fechaDMY(datos.period.from)} al ${fechaDMY(datos.period.to)}` : ""}
            </p>
          </div>
          {puedeExportar && datos ? (
            <Button variant="outline" size="sm" onClick={exportar} disabled={exportando} className="h-8 shrink-0 gap-1.5">
              {exportando ? <Spinner className="h-3.5 w-3.5" /> : <Download className="h-3.5 w-3.5" />}
              Exportar Excel
            </Button>
          ) : null}
        </div>
        <BarraDeFiltros
          periodo={(
            <Filtro etiqueta="Rango">
              <Segmentado opciones={RANGOS} elegida={rango} onElegir={elegir} etiqueta="Rango" />
            </Filtro>
          )}
        >
          <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end">
            <Filtro etiqueta="Desde">
              <input type="date" aria-label="Desde" className={SELECT_DE_FILTRO} value={propio.from} onChange={(e) => setPropio({ ...propio, from: e.target.value })} />
            </Filtro>
            <Filtro etiqueta="Hasta">
              <input type="date" aria-label="Hasta" className={SELECT_DE_FILTRO} value={propio.to} onChange={(e) => setPropio({ ...propio, to: e.target.value })} />
            </Filtro>
            <Button
              variant="outline"
              className="h-10"
              disabled={Boolean(problema)}
              title={problema || undefined}
              onClick={() => { setRango("propio"); setPedido({ from: propio.from, to: propio.to }); }}
            >
              Ver rango
            </Button>
          </div>
        </BarraDeFiltros>
      </header>

      {estado === "cargando" && !datos ? <div className="grid h-32 place-items-center"><Spinner /></div> : null}
      {estado === "error" ? (
        <p className="rounded-[10px] border border-brinks-danger-border bg-brinks-danger-soft px-3 py-2 text-[12.5px] text-brinks-danger">{error}</p>
      ) : null}

      {datos ? (
        <div className={cn("space-y-3", estado === "cargando" && "opacity-60")}>
          {datos.truncated ? (
            <p className="text-[11.5px] text-brinks-warning">El rango tiene más alertas de las que se pueden leer de una vez; acórtalo para verlas todas.</p>
          ) : null}
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
            <Cifra icono={CalendarCheck2} etiqueta="Días cerrados sin pendientes" valor={`${t.cleanDays} de ${t.days}`} detalle="Todas las oficinas cuadradas y ninguna alerta abierta" destacada />
            <Cifra icono={CheckCircle2} etiqueta="Cierres cuadrados" valor={formatMoney(t.balanced)} detalle={deTotal(t.balanced, t.closings)} />
            <Cifra icono={TriangleAlert} etiqueta="Alertas" valor={formatMoney(t.alerts)} detalle={`${t.open} abiertas · ${t.attended} atendidas`} />
            <Cifra
              icono={Wallet}
              etiqueta="Valor de las diferencias"
              valor={montoLegible(t.alertsValue)}
              palabras={enPalabras(t.alertsValue, { pesos: true })}
              detalle="Faltantes y sobrantes, sin compensarse"
            />
          </div>

          <Tarjeta titulo="Día por día" subtitulo="Un día abre su panel. La cuenta es la del cuadre del día.">
            <Tabla
              etiqueta="Cierre por día"
              columnas={[
                { titulo: "Fecha" }, { titulo: "Oficinas", numerica: true }, { titulo: "Cuadradas", numerica: true },
                { titulo: "Por revisar", numerica: true }, { titulo: "Incompletas", numerica: true }, { titulo: "Alertas", numerica: true },
                { titulo: "Valor", numerica: true }, { titulo: "Σ oficinas", numerica: true }, { titulo: "Cuenta", numerica: true },
                { titulo: "Diferencia", numerica: true },
              ]}
              vacia={datos.days.length ? null : "Sin cierres en el rango"}
            >
              {datos.days.map((d) => (
                <tr
                  key={d.date}
                  className={fila(true)}
                  onClick={() => navigate(`/business/backoffice/panel-cierres?fecha=${d.date}`)}
                  title={`Abrir el ${fechaDMY(d.date)} en el panel`}
                >
                  <td className={celda({ principal: true })}>{fechaDMY(d.date)}</td>
                  <td className={celda({ numerica: true })}>{d.offices || "—"}</td>
                  <td className={celda({ numerica: true })}>{d.balanced}</td>
                  <td className={celda({ numerica: true })}>{d.review || "—"}</td>
                  <td className={celda({ numerica: true })}>{d.incomplete || "—"}</td>
                  <td className={celda({ numerica: true, tono: d.open ? "text-brinks-warning" : undefined })}>{d.alerts ? `${d.alerts} (${d.open} abiertas)` : "—"}</td>
                  <td className={celda({ numerica: true })}>{d.alertsValue ? montoLegible(d.alertsValue) : "—"}</td>
                  <td className={celda({ numerica: true })}>{monto(d.reconciliation?.totalOffices)}</td>
                  <td className={celda({ numerica: true })}>{monto(d.reconciliation?.account)}</td>
                  <td className={celda({ numerica: true, tono: d.reconciliation?.difference ? "text-brinks-danger" : undefined })}>{monto(d.reconciliation?.difference)}</td>
                </tr>
              ))}
            </Tabla>
          </Tarjeta>

          <div className="grid gap-3 xl:grid-cols-3">
            <Tarjeta titulo="Alertas por día" subtitulo="Abiertas y atendidas">
              <LugarDeGrafica vacio={t.alerts ? null : "Sin alertas en el rango"} alto="h-[240px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={puntosDeAlertas(datos.days)} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--brinks-border)" vertical={false} />
                    <XAxis dataKey="etiqueta" {...EJE} interval="preserveStartEnd" minTickGap={8} />
                    <YAxis allowDecimals={false} {...EJE} width={32} />
                    <Tooltip cursor={{ fill: "var(--brinks-surface)" }} />
                    <Legend verticalAlign="top" align="left" wrapperStyle={{ fontSize: 11.5, paddingBottom: 6 }} formatter={leyendaLegible} />
                    <Bar dataKey="abiertas" name="Abiertas" stackId="a" fill="var(--brinks-warning)" isAnimationActive={false} />
                    <Bar dataKey="atendidas" name="Atendidas" stackId="a" fill="hsl(var(--primary))" radius={[3, 3, 0, 0]} isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              </LugarDeGrafica>
            </Tarjeta>
            <Tarjeta titulo="Oficinas con más alertas" subtitulo="Para seguimiento interno de la operación">
              <Tabla
                etiqueta="Oficinas con más alertas"
                alto="max-h-[300px]"
                columnas={[{ titulo: "Oficina" }, { titulo: "Alertas", numerica: true }, { titulo: "Días", numerica: true }, { titulo: "Valor", numerica: true }]}
                vacia={datos.byOffice.length ? null : "Sin alertas en el rango"}
              >
                {datos.byOffice.map((o) => (
                  <tr key={o.officeCode} className={fila()}>
                    <td className={celda({ principal: true })}>
                      {o.officeName}
                      <span className="block text-[11.5px] font-normal text-brinks-muted-foreground">{o.region}</span>
                    </td>
                    <td className={celda({ numerica: true })}>
                      {o.alerts}
                      {o.open ? <span className="block text-[11.5px] text-brinks-warning">{o.open} abiertas</span> : null}
                    </td>
                    <td className={celda({ numerica: true })}>{o.days}</td>
                    <td className={celda({ numerica: true })}>{o.value ? montoLegible(o.value) : "—"}</td>
                  </tr>
                ))}
              </Tabla>
            </Tarjeta>
            <Tarjeta titulo="Alertas por tipo">
              {datos.byRule.length ? (
                <ul className="max-h-[300px] space-y-2.5 overflow-y-auto pr-1">
                  {datos.byRule.map((r) => (
                    <li key={r.ruleKey} className="flex items-start justify-between gap-3">
                      <span className="min-w-0 break-words text-[12.5px] font-medium text-brinks-foreground">{nombreDeAlerta(nombres, r.ruleKey)}</span>
                      <span className="shrink-0 text-right text-[12px] tabular-nums text-brinks-muted-foreground">
                        <span className="block font-medium text-brinks-foreground">{r.alerts}</span>
                        {r.open ? <span className="block">{r.open} abiertas</span> : null}
                        {r.value ? <span className="block">{montoLegible(r.value)}</span> : null}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : <p className="py-6 text-center text-[12.5px] text-brinks-muted-foreground">Sin alertas en el rango</p>}
            </Tarjeta>
          </div>
        </div>
      ) : null}
    </section>
  );
}
